import get from 'lodash/get'

/**
 * Pure data pipeline behind Table: filter, then sort, then paginate.
 * Extracted so the ordering rules can be unit-tested without rendering.
 */

export const readCell = (record, column) => {
  if (column.dataIndex == null) return undefined
  return Array.isArray(column.dataIndex)
    ? get(record, column.dataIndex)
    : get(record, String(column.dataIndex).split('.'))
}

export const resolveRowKey = (record, index, rowKey) => {
  if (typeof rowKey === 'function') return rowKey(record, index)
  if (rowKey && record?.[rowKey] != null) return record[rowKey]
  return record?.key ?? record?.id ?? index
}

/** antd's default comparison when `sorter: true` is used without a function. */
const defaultCompare = (a, b) => {
  if (a == null && b == null) return 0
  if (a == null) return -1
  if (b == null) return 1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), 'fr', { numeric: true, sensitivity: 'base' })
}

export const applyFilters = (data, columns, filters) => {
  const active = Object.entries(filters).filter(([, values]) => values?.length)
  if (active.length === 0) return data

  return data.filter((record) =>
    active.every(([columnKey, values]) => {
      const column = columns.find((candidate) => (candidate.key ?? candidate.dataIndex) === columnKey)
      if (!column) return true
      if (typeof column.onFilter === 'function') {
        return values.some((value) => column.onFilter(value, record))
      }
      const cell = readCell(record, column)
      return values.some((value) => String(cell) === String(value))
    })
  )
}

export const applySorter = (data, columns, sorter) => {
  if (!sorter?.columnKey || !sorter.order) return data
  const column = columns.find((candidate) => (candidate.key ?? candidate.dataIndex) === sorter.columnKey)
  if (!column?.sorter) return data

  const compare =
    typeof column.sorter === 'function'
      ? column.sorter
      : typeof column.sorter === 'object' && typeof column.sorter.compare === 'function'
        ? column.sorter.compare
        : (a, b) => defaultCompare(readCell(a, column), readCell(b, column))

  const direction = sorter.order === 'descend' ? -1 : 1
  // Copy first: antd never mutates dataSource.
  return [...data].sort((a, b) => compare(a, b) * direction)
}

export const applyPagination = (data, pagination) => {
  if (pagination === false || !pagination) return data
  const pageSize = pagination.pageSize ?? 10
  const current = pagination.current ?? 1
  return data.slice((current - 1) * pageSize, current * pageSize)
}

/** antd cycles a sortable header ascend -> descend -> off. */
export const nextSortOrder = (current) => {
  if (current === 'ascend') return 'descend'
  if (current === 'descend') return null
  return 'ascend'
}

export const buildViewData = ({ dataSource, columns, filters, sorter, pagination }) => {
  const filtered = applyFilters(dataSource, columns, filters)
  const sorted = applySorter(filtered, columns, sorter)
  return { rows: applyPagination(sorted, pagination), total: filtered.length, sorted }
}

export { defaultCompare }
